import { useState, useEffect } from "react";
import { Activity, Flame, AlertTriangle, UserX, Calendar, RefreshCw, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function dagGeleden(iso) {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (diff === 0) return "vandaag";
  if (diff === 1) return "gisteren";
  return `${diff} dagen geleden`;
}

function StatusBadge({ sporter }) {
  if (sporter.actief_deze_week) {
    return (
      <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg"
        style={{ background: "rgba(16,185,129,0.12)", color: "#10b981" }}>
        <Flame size={10} /> Actief
      </span>
    );
  }
  if (!sporter.laatste_training) {
    return (
      <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg"
        style={{ background: "rgba(113,113,122,0.15)", color: "#52525b" }}>
        <UserX size={10} /> Nooit
      </span>
    );
  }
  const dagen = Math.floor((Date.now() - new Date(sporter.laatste_training)) / 86400000);
  if (dagen > 14) {
    return (
      <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg"
        style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444" }}>
        <AlertTriangle size={10} /> {dagen}d weg
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg"
      style={{ background: "rgba(245,158,11,0.12)", color: "#f59e0b" }}>
      <Calendar size={10} /> {dagen}d geleden
    </span>
  );
}

export default function ActiviteitTab({ token, bc, onSelectSporter }) {
  const [sporters, setSporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("alle"); // alle | actief | inactief | nooit
  const primary = bc?.primary || "#C9A84C";

  const laadActiviteit = () => {
    setLoading(true);
    fetch(`${API}/api/company/sporters/activiteit`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { sporters: [] })
      .then(d => setSporters(d.sporters || []))
      .catch(() => setSporters([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { laadActiviteit(); }, [token]);

  const gefilterd = sporters.filter(s => {
    if (filter === "actief") return s.actief_deze_week;
    if (filter === "inactief") return !s.actief_deze_week && s.laatste_training;
    if (filter === "nooit") return !s.laatste_training;
    return true;
  });

  const actief = sporters.filter(s => s.actief_deze_week).length;
  const inactief = sporters.filter(s => !s.actief_deze_week && s.laatste_training).length;
  const nooit = sporters.filter(s => !s.laatste_training).length;

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: primary }}>
            Afgelopen 7 dagen
          </p>
          <h2 className="text-xl font-bold text-white">Sporter Activiteit</h2>
          <p className="text-zinc-500 text-sm mt-0.5">Wie heeft er getraind deze week?</p>
        </div>
        <button
          onClick={laadActiviteit}
          data-testid="activiteit-refresh"
          className="p-2 rounded-xl border border-white/8 text-zinc-500 hover:text-white transition-colors"
        >
          <RefreshCw size={15} />
        </button>
      </div>

      {/* KPI balk */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <button
          onClick={() => setFilter(filter === "actief" ? "alle" : "actief")}
          data-testid="filter-actief"
          className={`rounded-2xl p-4 text-center border transition-all ${filter === "actief" ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/5 bg-[#0f0f0f] hover:border-emerald-500/20"}`}
        >
          <Flame size={18} className="mx-auto mb-1.5" style={{ color: "#10b981" }} />
          <p className="text-2xl font-black text-white">{actief}</p>
          <p className="text-[10px] font-bold uppercase tracking-wide mt-0.5" style={{ color: "#10b981" }}>Actief</p>
        </button>
        <button
          onClick={() => setFilter(filter === "inactief" ? "alle" : "inactief")}
          data-testid="filter-inactief"
          className={`rounded-2xl p-4 text-center border transition-all ${filter === "inactief" ? "border-amber-500/40 bg-amber-500/10" : "border-white/5 bg-[#0f0f0f] hover:border-amber-500/20"}`}
        >
          <AlertTriangle size={18} className="mx-auto mb-1.5" style={{ color: "#f59e0b" }} />
          <p className="text-2xl font-black text-white">{inactief}</p>
          <p className="text-[10px] font-bold uppercase tracking-wide mt-0.5" style={{ color: "#f59e0b" }}>Inactief</p>
        </button>
        <button
          onClick={() => setFilter(filter === "nooit" ? "alle" : "nooit")}
          data-testid="filter-nooit"
          className={`rounded-2xl p-4 text-center border transition-all ${filter === "nooit" ? "border-zinc-500/40 bg-zinc-500/10" : "border-white/5 bg-[#0f0f0f] hover:border-zinc-500/20"}`}
        >
          <UserX size={18} className="mx-auto mb-1.5 text-zinc-600" />
          <p className="text-2xl font-black text-white">{nooit}</p>
          <p className="text-[10px] font-bold uppercase tracking-wide mt-0.5 text-zinc-600">Nooit</p>
        </button>
      </div>

      {/* Sporterslijst */}
      {loading ? (
        <div className="text-center py-12 text-zinc-600 text-sm">Laden...</div>
      ) : gefilterd.length === 0 ? (
        <div className="text-center py-12 text-zinc-600 text-sm">Geen sporters gevonden</div>
      ) : (
        <div className="space-y-2">
          {gefilterd.map(s => (
            <button
              key={s.id}
              data-testid={`sporter-activiteit-${s.id}`}
              onClick={() => onSelectSporter?.(s.id)}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-white/5 bg-[#0f0f0f] hover:border-white/10 hover:bg-[#141414] transition-all text-left group"
            >
              {/* Avatar */}
              <div className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center font-black text-sm"
                style={{
                  background: s.actief_deze_week ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.05)",
                  color: s.actief_deze_week ? "#10b981" : "#52525b",
                }}>
                {(s.naam || s.email || "?")[0].toUpperCase()}
              </div>

              {/* Naam + meta */}
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm truncate">{s.naam || s.email}</p>
                <p className="text-zinc-600 text-xs mt-0.5">
                  {s.actief_deze_week
                    ? `${s.trainingen_deze_week}× getraind deze week`
                    : s.laatste_training
                      ? `Laatste training: ${dagGeleden(s.laatste_training)}`
                      : "Nog nooit getraind"
                  }
                </p>
              </div>

              {/* Status badge */}
              <StatusBadge sporter={s} />

              <ChevronRight size={14} className="text-zinc-700 group-hover:text-zinc-500 transition-colors shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Legenda */}
      <div className="mt-6 flex flex-wrap gap-3 text-xs text-zinc-600">
        <span className="flex items-center gap-1"><Flame size={10} style={{ color: "#10b981" }} /> Getraind in afgelopen 7 dagen</span>
        <span className="flex items-center gap-1"><AlertTriangle size={10} style={{ color: "#f59e0b" }} /> Niet getraind (7–14 dagen)</span>
        <span className="flex items-center gap-1"><AlertTriangle size={10} style={{ color: "#ef4444" }} /> Meer dan 14 dagen weg</span>
      </div>
    </div>
  );
}
